import { useGetProcessQuery } from "@/services/backend/processes/process/service";
import ConfirmationDialog from "@/shared/components/domain/confirmation/confirmation-dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { Button } from "@/shared/components/ui/button";
import { ReactElement, useEffect } from "react";
import { v4 } from "uuid";
import { useCreateDifferentiationMutation } from "@/services/backend/differentiation/service";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useNavigate } from "react-router";

interface CreateCountingButtonProps {
  processId: string;
  customerId: string;
  countingId: string;
  disabled?: boolean;
}

export default function CreateDifferentiationModal({
  processId,
  customerId,
  countingId,
  disabled,
}: CreateCountingButtonProps): ReactElement {
  const { user } = useAuth();
  const nav = useNavigate();
  const [createDifferentiation, { data, error, reset, isLoading, isSuccess }] =
    useCreateDifferentiationMutation();
  const { resetNotReadyError } = useErrArtefactNotReady();
  useSetErrArtefactNotReadyFromRtk(error, () => {
    reset();
  });

  useEffect(() => {
    if (isSuccess && data != null && data.differentiation != null) {
      nav(
        `/processes/${processId}/differentiation/${data.differentiation.id}/differentiation`,
      );
    }
  }, [isSuccess, data, nav, processId]);

  const {
    data: process,
    isLoading: processIsLoading,
    error: processError,
  } = useGetProcessQuery({
    id: processId ?? "",
  });

  if (processIsLoading) {
    return <div />;
  }

  if (processError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    if (!process) {
      return;
    }

    createDifferentiation({
      id: v4(),
      customerId,
      processId,
      countingId,
      createdBy: user!.id,
    });

    resetNotReadyError();
  };

  return (
    <ConfirmationDialog
      trigger={
        <Button disabled={disabled}>
          <p>Differenzierung starten</p>
        </Button>
      }
      title="Differenzierung erstellen"
      description="Hiermit wird die Differenzierung gestartet."
      onConfirm={onConfirm}
    />
  );
}
